/**
 * Enabled-modules context object, types, constants and hooks (per-agency
 * package gating, D4-PKG-003).
 *
 * Split out of ModulesContext.tsx so that the .tsx module exports ONLY the
 * ModulesProvider component (keeps React Fast Refresh + the
 * `react-refresh/only-export-components` lint rule clean).
 */
import { createContext, useContext } from "react";

// Keep in sync with backend common/modules.py ALL_MODULES.
export type ModuleKey =
  | "financial"
  | "farm"
  | "messaging"
  | "fica"
  | "training"
  | "documents"
  | "client_portal";

export const ALL_MODULES: ModuleKey[] = [
  "financial",
  "farm",
  "messaging",
  "fica",
  "training",
  "documents",
  "client_portal",
];

export interface ModulesConfig {
  all_modules: ModuleKey[];
  enabled_modules: ModuleKey[];
}

export interface ModulesContextType {
  enabledModules: ModuleKey[];
  isEnabled: (module: ModuleKey) => boolean;
  loading: boolean;
}

export const DEFAULT_MODULES_CONFIG: ModulesConfig = {
  all_modules: ALL_MODULES,
  enabled_modules: ALL_MODULES,
};

export const ModulesContext = createContext<ModulesContextType>({
  enabledModules: ALL_MODULES,
  isEnabled: () => true,
  loading: true,
});

export function useModules(): ModulesContextType {
  return useContext(ModulesContext);
}

/** Convenience hook: returns true if the given module is enabled. */
export function useModuleEnabled(module: ModuleKey): boolean {
  return useContext(ModulesContext).isEnabled(module);
}
